import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  HomeIcon,
  MegaphoneIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useAdminNoIndex } from '../hooks/useAdminNoIndex';
import { useSessionTimeout } from '../hooks/useSessionTimeout';

const NAV = [
  { to: '/admin', end: true, label: 'Özet', icon: HomeIcon },
  { to: '/admin/sentence-info', end: false, label: 'Afirmasyonlar', icon: SparklesIcon },
  { to: '/admin/affirmation-plans', end: false, label: '21 Günlük Programlar', icon: ClipboardDocumentListIcon },
  { to: '/admin/support', end: false, label: 'Destek', icon: ChatBubbleLeftRightIcon },
  { to: '/admin/winback', end: false, label: 'Abone Kazanımı', icon: MegaphoneIcon },
];

const AdminLayout: React.FC = () => {
  useAdminNoIndex();
  const { logout, user } = useAuth();
  useSessionTimeout();
  const [mobileOpen, setMobileOpen] = useState(false);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-purple-600 text-white'
        : 'text-gray-700 hover:bg-purple-50 hover:text-purple-800'
    }`;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-100 px-4 py-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-purple-600">Mor Kıvılcım</div>
        <div className="mt-1 text-lg font-bold text-gray-900">Admin Panel</div>
        {user && (
          <div className="mt-2 truncate text-xs text-gray-500">{user.name || user.email}</div>
        )}
      </div>

      <nav className="flex-1 space-y-1 p-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={linkClass}
            onClick={() => setMobileOpen(false)}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-gray-100 p-3">
        <button
          type="button"
          onClick={() => logout()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
          Çıkış yap
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-gray-200 bg-white lg:block">
        <div className="sticky top-0 h-screen overflow-y-auto">{sidebar}</div>
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
        <div>
          <div className="text-sm font-bold text-gray-900">Admin Panel</div>
          <div className="text-xs text-gray-500 truncate max-w-[200px]">{user?.email}</div>
        </div>
        <button
          type="button"
          className="rounded-lg border border-gray-200 p-2 text-gray-700"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Menüyü kapat"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl">
            <div className="flex justify-end p-3">
              <button
                type="button"
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                onClick={() => setMobileOpen(false)}
                aria-label="Kapat"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {sidebar}
          </div>
        </div>
      )}

      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
